import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { AlertTriangle, CheckCircle, XCircle, Clock, TrendingDown, RefreshCw, Database, Zap } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const sfColors = { blue: "#29B5E8", green: "#22C55E", amber: "#F59E0B", red: "#EF4444", rose: "#F43F5E", orange: "#F97316" };

function StatCard({ label, value, icon: Icon, accent, delay }) {
  return (
    <div className="sf-card-accent p-5 card-hover animate-fade-up" style={{ animationDelay: `${delay}ms` }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] uppercase tracking-[0.15em] font-semibold" style={{ color: "#6B7280" }}>{label}</span>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}12` }}>
          <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        </div>
      </div>
      <div className="text-[32px] font-extrabold tracking-tight text-[#2D3142] leading-none">{value}</div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try { const { data: d } = await api.get("/overview"); setData(d); } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 15000); return () => clearInterval(i); }, [fetchData]);

  if (loading) return (
    <div className="p-6 space-y-4" data-testid="dashboard-loading">
      <Skeleton className="h-8 w-48 bg-white/5" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-28 bg-white/5 rounded-xl" />)}</div>
    </div>
  );
  if (!data) return <div className="p-6 text-center font-mono" style={{ color: "#9CA3AF" }}>[ NO DATA ]</div>;

  const sevData = Object.entries(data.incidents_by_severity || {}).map(([k, v]) => ({
    name: k.charAt(0).toUpperCase() + k.slice(1), count: v,
    fill: k === "critical" ? sfColors.red : k === "high" ? sfColors.amber : k === "medium" ? "#EAB308" : sfColors.blue
  }));

  const chartTooltip = { background: "#FFFFFF", border: "1px solid #E8E4DF", borderRadius: "8px", fontSize: "12px", fontFamily: "JetBrains Mono", color: "#C0CDD8" };

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-[#2D3142]">Overview</h1>
          <p className="text-[13px] mt-0.5" style={{ color: "#6B7280" }}>Pipeline health & incident summary</p>
        </div>
        <div className="flex items-center gap-2.5 px-3.5 py-2 rounded-full" style={{ background: "rgba(45,106,79,0.06)", border: "1px solid rgba(45,106,79,0.12)" }}>
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#22C55E" }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: "#22C55E" }} />
          </span>
          <span className="text-[11px] font-bold tracking-wide" style={{ color: "#2D6A4F" }}>LIVE</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-grid">
        <StatCard label="Total Pipelines" value={data.total_pipelines} icon={Database} accent={sfColors.blue} delay={0} />
        <StatCard label="Healthy" value={data.healthy} icon={CheckCircle} accent={sfColors.green} delay={60} />
        <StatCard label="Warning" value={data.warning} icon={AlertTriangle} accent={sfColors.amber} delay={120} />
        <StatCard label="Critical" value={data.critical} icon={XCircle} accent={sfColors.red} delay={180} />
        <StatCard label="SLA Breaches" value={data.sla_breaches} icon={Clock} accent={sfColors.rose} delay={240} />
        <StatCard label="Open Incidents" value={data.open_incidents} icon={TrendingDown} accent={sfColors.orange} delay={300} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="sf-card p-5 animate-fade-up" style={{ animationDelay: "350ms" }} data-testid="anomaly-trend-chart">
          <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-4" style={{ color: "rgba(30,58,95,0.6)" }}>Anomaly Trend (30 Days)</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.anomaly_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} tickFormatter={(v) => v.slice(5)} stroke="#E8E4DF" />
                <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} stroke="#E8E4DF" />
                <Tooltip contentStyle={chartTooltip} />
                <Line type="monotone" dataKey="anomalies" stroke={sfColors.red} strokeWidth={2} dot={{ fill: sfColors.red, r: 2, strokeWidth: 0 }} activeDot={{ r: 5, fill: sfColors.red, stroke: "#11192B", strokeWidth: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="sf-card p-5 animate-fade-up" style={{ animationDelay: "400ms" }} data-testid="severity-chart">
          <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold mb-4" style={{ color: "rgba(30,58,95,0.6)" }}>Incidents by Severity</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sevData} layout="vertical" barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} stroke="#E8E4DF" />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fontFamily: "JetBrains Mono", fill: "#9CA3AF" }} width={60} stroke="#E8E4DF" />
                <Tooltip contentStyle={chartTooltip} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>{sevData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="sf-card overflow-hidden animate-fade-up" style={{ animationDelay: "450ms" }} data-testid="top-failing-pipelines">
          <div className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: "1px solid #E8E4DF" }}>
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>Top Failing Pipelines</h3>
          </div>
          <div>
            {(data.top_failing_pipelines || []).map((p, i) => (
              <Link key={p.pipeline_id} to={`/pipelines/${p.pipeline_id}`} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-all group" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }} data-testid={`failing-pipeline-${p.pipeline_id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono w-4" style={{ color: "#9CA3AF" }}>{i + 1}.</span>
                  <span className={`status-dot ${p.status}`} />
                  <span className="text-[13px] font-medium font-mono text-[#8899AA] group-hover:text-[#2D3142] transition-colors">{p.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="mono-data" style={{ color: "#6B7280" }}>{p.freshness_lag_minutes}m</span>
                  <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: p.status === "critical" ? "rgba(190,52,85,0.12)" : "rgba(231,111,81,0.12)", color: p.status === "critical" ? "#F87171" : "#FBBF24" }}>{p.status}</Badge>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div className="sf-card overflow-hidden animate-fade-up" style={{ animationDelay: "500ms" }} data-testid="recent-remediations">
          <div className="px-5 py-3.5 flex items-center justify-between" style={{ borderBottom: "1px solid #E8E4DF" }}>
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold" style={{ color: "rgba(30,58,95,0.6)" }}>Recent Remediations</h3>
            <Zap className="w-3.5 h-3.5" style={{ color: "rgba(30,58,95,0.2)" }} />
          </div>
          <div>
            {(data.recent_remediations || []).map((r) => (
              <div key={r.action_id} className="flex items-center justify-between px-5 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.03)" }} data-testid={`remediation-${r.action_id}`}>
                <div className="flex items-center gap-3">
                  <RefreshCw className="w-3.5 h-3.5" style={{ color: "#9CA3AF" }} />
                  <div><span className="text-[13px] font-medium text-[#8899AA]">{r.action_type?.replace(/_/g, " ")}</span><span className="text-[10px] ml-2 font-mono" style={{ color: "#9CA3AF" }}>{r.incident_id}</span></div>
                </div>
                <Badge className="text-[9px] uppercase font-bold border-0 rounded-md" style={{ background: r.status === "approved" ? "rgba(45,106,79,0.12)" : r.status === "rejected" ? "rgba(190,52,85,0.12)" : "rgba(255,255,255,0.05)", color: r.status === "approved" ? "#4ADE80" : r.status === "rejected" ? "#F87171" : "#5A6B82" }}>{r.status}</Badge>
              </div>
            ))}
            {(!data.recent_remediations || data.recent_remediations.length === 0) && <div className="px-5 py-8 text-center text-sm font-mono" style={{ color: "#9CA3AF" }}>[ No remediations ]</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
